import React, { useState } from "react";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

const Pill = ({ label, value, testId }) => {
  const tone = value === true ? "bg-emerald-100 text-emerald-800" :
               value === false ? "bg-rose-100 text-rose-800" :
               "bg-slate-100 text-slate-700";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      <span className="opacity-70">{label}:</span>
      <span className="font-mono">{String(value)}</span>
    </span>
  );
};

/**
 * VerifyMembershipForm — Sub-sprint D-β-α.
 *
 * Read-only verification of a user's RL membership. Single user_id
 * input + a button that POSTs `verify-membership` and renders the
 * resulting flags as colored pills.
 */
export const VerifyMembershipForm = ({ dataTestId = "rl-ops-verify-form" }) => {
  const t = useRacketLabAdminT();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const { run, loading } = useAdminMutation();

  const onSubmit = async () => {
    setErr(null);
    setResult(null);
    if (!userId.trim()) {
      setErr({ detail: t("ops.verify.user_id_required") });
      return;
    }
    try {
      const r = await run("post", "/admin/racket-lab/_ops/verify-membership", {
        user_id: userId.trim(),
        role: role.trim() || undefined,
      });
      setResult(r);
    } catch (e) {
      setErr({ status: e?.status, detail: e?.detail || t("ops.common.error_unknown") });
    }
  };

  return (
    <div data-testid={dataTestId} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-3">
      <h3 className="text-sm font-medium text-slate-800">{t("ops.verify.title")}</h3>
      <div className="flex flex-col gap-2 md:flex-row md:items-end">
        <label className="flex flex-1 flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.verify.user_id_label")}</span>
          <input
            data-testid={`${dataTestId}-user-id`}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loading}
            placeholder={t("ops.verify.user_id_placeholder")}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.verify.role_label_optional")}</span>
          <input
            data-testid={`${dataTestId}-role`}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={loading}
            placeholder={t("ops.verify.role_placeholder")}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          data-testid={`${dataTestId}-submit`}
          onClick={onSubmit}
          disabled={!userId.trim() || loading}
          className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${userId.trim() && !loading ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"}`}
        >
          {t("ops.verify.submit")}
        </button>
      </div>

      {err && (
        <div data-testid={`${dataTestId}-error`} className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {err.status || ""}: {err.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {result && (
        <div data-testid={`${dataTestId}-result`} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
          <Pill label="found" value={result.found} testId={`${dataTestId}-result-found`} />
          <Pill label="is_active_status" value={result.is_active_status} testId={`${dataTestId}-result-active`} />
          <Pill label="is_runtime_role" value={result.is_runtime_role} testId={`${dataTestId}-result-runtime-role`} />
          {result.role && (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-800 font-mono">
              role: {result.role}
            </span>
          )}
          {result.status && (
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] text-slate-700 font-mono">
              status: {result.status}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VerifyMembershipForm;
